import { Injectable, inject } from '@angular/core';
import { Observable, defer, firstValueFrom, map, of, switchMap } from 'rxjs';
import { Utilisateur } from '../../_interfaces/utilisateur';
import {
  Auth,
  UserCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { FirebaseService } from '../../_firebase/firebase.service';
import { UtilisateurService } from '../utilisateur/utilisateur.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private firebase: FirebaseService = inject(FirebaseService);
  private auth: Auth = this.firebase.auth;
  private utilisateurService: UtilisateurService = inject(UtilisateurService);

  public isAuth$ = defer(() => this.firebase.authState().pipe(map((user: any) => user !== null)));
  public isAdmin$: Observable<boolean> = defer(() => this.firebase.authState().pipe(
    switchMap(
      (user: any) => {
        if (user != null) {
          return this.utilisateurService.readUtilisateurByUid(user.uid).pipe(
            map((utilisateur: Utilisateur) => (utilisateur.role === 'admin') as boolean)
          );
        } else {
          return of(false);
        }
      }
    ))
  );
  public currentUid$ = this.firebase.authState().pipe(map((user: any) => user?.uid));
  public currentUser$: Observable<Utilisateur | null> = this.firebase.authState().pipe(
    switchMap((user: any) => {
      if (user) {
        return this.utilisateurService.readUtilisateurByUid(user.uid);
      } else {
        return of(null);
      }
    })
  );

  constructor() {
    this.currentUser$.subscribe(utilisateur => {
      if (utilisateur?.disabled) {
        this.logout();
      }
    });
  }

  async login(email: string, password: string): Promise<'ok' | 'invalid' | 'disabled'> {
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const utilisateur = await firstValueFrom(this.utilisateurService.readUtilisateurByUid(userCredential.user.uid));
      if (utilisateur.disabled) {
        await signOut(this.auth);
        return 'disabled';
      }
      return 'ok';
    } catch {
      return 'invalid';
    }
  }

  logout() {
    signOut(this.auth).catch((error) => {
      console.error(error);
    });
  }

  async register(email: string, password: string, firstName: string, lastName: string): Promise<boolean> {
    try {
      const alreadyExists = await firstValueFrom(this.utilisateurService.utilisateurExistWithMail(email));
      if (alreadyExists) {
        return false;
      }
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;
      await this.utilisateurService.createUtilisateur({
        uid: user.uid,
        email: email,
        firstName: firstName,
        lastName: lastName,
        role: 'user'
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async isLogged() {
    return await this.firebase.authState();
  }

  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }
}
